import { useCallback, useEffect, useState } from "react";
import { LogOut, Loader2, ShieldCheck } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { BrandMark } from "@/components/AppShell";
import { adminApi, ADMIN_TOKEN_KEY, errMsg } from "@/lib/api";
import AdminAccounts from "@/pages/admin/AdminAccounts";
import AdminRequests from "@/pages/admin/AdminRequests";
import AdminPlans from "@/pages/admin/AdminPlans";
import AdminContent from "@/pages/admin/AdminContent";
import AdminWhatsApp from "@/pages/admin/AdminWhatsApp";
import { MaintenanceToggle } from "@/pages/admin/MaintenanceToggle";
import AdminUsers from "@/pages/admin/AdminUsers";
import AdminManual, { ManualButton } from "@/pages/admin/AdminManual";
import AdminData from "@/pages/admin/AdminData";
import AdminAudit from "@/pages/admin/AdminAudit";
import AdminMessages from "@/pages/admin/AdminMessages";
import AdminResellers from "@/pages/admin/AdminResellers";

function AdminLogin({ onIn }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const { data } = await adminApi.post("/auth/login", { email, password });
      localStorage.setItem(ADMIN_TOKEN_KEY, data.token);
      onIn(data.user);
    } catch (err) {
      setError(errMsg(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="grain grid min-h-[100dvh] place-items-center bg-slate-950 px-4">
      <form onSubmit={submit} className="w-full max-w-sm rounded-2xl bg-white p-7 shadow-2xl" data-testid="admin-login-form">
        <BrandMark light={false} />
        <div className="mt-6 flex items-center gap-2 text-amber-600"><ShieldCheck className="h-4 w-4" /><span className="eyebrow !text-amber-700">Super admin</span></div>
        <h1 className="mt-1 font-display text-2xl font-extrabold text-slate-900">Admin console</h1>
        <div className="mt-6 space-y-4">
          <div className="space-y-1.5"><Label htmlFor="ae">Email</Label><Input id="ae" type="email" className="h-11" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="admin-email-input" /></div>
          <div className="space-y-1.5"><Label htmlFor="ap">Password</Label><Input id="ap" type="password" className="h-11" value={password} onChange={(e) => setPassword(e.target.value)} data-testid="admin-password-input" /></div>
          {error && <div className="rounded-lg bg-rose-50 px-3 py-2 text-sm font-medium text-rose-700" data-testid="admin-login-error">{error}</div>}
          <button disabled={!email || !password || busy} data-testid="admin-login-submit" className="flex h-11 w-full items-center justify-center gap-2 rounded-xl bg-slate-900 font-semibold text-white disabled:bg-slate-300">
            {busy && <Loader2 className="h-4 w-4 animate-spin" />} Sign in
          </button>
        </div>
      </form>
    </div>
  );
}

const TABS = [["accounts", "Accounts"], ["users", "Users"], ["data", "Data"], ["requests", "Upgrade requests"], ["messages", "Messages"], ["resellers", "Resellers"], ["plans", "Plans"], ["content", "Content"], ["whatsapp", "WhatsApp"], ["audit", "Audit log"], ["manual", "User manual"]];

export default function AdminApp() {
  const [admin, setAdmin] = useState(null);
  const [tab, setTab] = useState(() => new URLSearchParams(window.location.search).get("tab") || "accounts");

  const check = useCallback(async () => {
    if (!localStorage.getItem(ADMIN_TOKEN_KEY)) return setAdmin(false);
    try {
      setAdmin((await adminApi.get("/auth/me")).data);
    } catch {
      localStorage.removeItem(ADMIN_TOKEN_KEY);
      setAdmin(false);
    }
  }, []);
  useEffect(() => { check(); }, [check]);

  if (admin === null) return null;
  if (!admin) return <AdminLogin onIn={setAdmin} />;

  return (
    <div className="min-h-[100dvh] bg-slate-50" data-testid="admin-console">
      <header className="grain sticky top-0 z-30 bg-slate-950">
        <div className="mx-auto flex h-14 max-w-7xl items-center gap-4 px-4 sm:px-8">
          <BrandMark />
          <span className="rounded-full bg-amber-500/15 px-2.5 py-1 font-mono text-[10px] font-bold uppercase tracking-widest text-amber-400">Admin</span>
          <span className="ml-auto hidden text-sm text-slate-400 md:block" data-testid="admin-email">{admin.email}</span>
          <MaintenanceToggle />
          <ManualButton />
          <button onClick={() => { localStorage.removeItem(ADMIN_TOKEN_KEY); setAdmin(false); }} data-testid="admin-logout" className="grid h-10 w-10 place-items-center rounded-lg text-slate-400 hover:bg-white/10 hover:text-white" aria-label="Log out">
            <LogOut className="h-4 w-4" />
          </button>
        </div>
        <nav className="mx-auto flex max-w-7xl gap-1 overflow-x-auto px-4 sm:px-8">
          {TABS.map(([k, l]) => (
            <button key={k} onClick={() => setTab(k)} data-testid={`admin-tab-${k}`} className={`h-11 shrink-0 border-b-2 px-3 text-sm font-semibold ${tab === k ? "border-amber-500 text-white" : "border-transparent text-slate-400 hover:text-white"}`}>{l}</button>
          ))}
        </nav>
      </header>
      <main className="mx-auto max-w-7xl px-4 py-8 sm:px-8">
        {tab === "accounts" && <AdminAccounts />}
        {tab === "users" && <AdminUsers />}
        {tab === "requests" && <AdminRequests />}
        {tab === "plans" && <AdminPlans />}
        {tab === "content" && <AdminContent onPlans={() => setTab("plans")} />}
        {tab === "whatsapp" && <AdminWhatsApp />}
        {tab === "resellers" && <AdminResellers />}
        {tab === "data" && <AdminData />}
        {tab === "audit" && <AdminAudit />}
        {tab === "messages" && <AdminMessages />}
        {tab === "manual" && <AdminManual />}
      </main>
    </div>
  );
}
